import { Filter, LayoutGrid, Truck } from "lucide-react";

import { cn } from "@/lib/cn";
import type { Category } from "@/repositories/catalogueRepository";

/**
 * The three dropdowns from the design.
 *
 * Native `<select>` elements rather than a custom listbox, on purpose: on a
 * touchscreen till Android and iOS both render a full-height native picker,
 * which is far easier to hit than a styled dropdown, and it works with a
 * keyboard and a screen reader without any code from us.
 *
 * The Suppliers filter is present and disabled. Suppliers are Phase 8, and the
 * honest thing is a control that says so rather than one silently missing from a
 * row the design shows with three.
 */
export function StockFilters({
  categories,
  category,
  status,
  onCategory,
  onStatus,
}: {
  categories: Category[];
  category: string;
  status: string;
  onCategory: (value: string) => void;
  onStatus: (value: string) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <Select
        icon={LayoutGrid}
        label="Category"
        value={category}
        onChange={onCategory}
        options={[
          { value: "", label: "All categories" },
          ...categories.map((item) => ({ value: item.id, label: item.name })),
        ]}
      />

      <Select
        icon={Truck}
        label="Supplier"
        value=""
        onChange={() => {}}
        disabled
        title="Suppliers arrive in Phase 8"
        options={[{ value: "", label: "All suppliers" }]}
      />

      <Select
        icon={Filter}
        label="Stock status"
        value={status}
        onChange={onStatus}
        options={[
          { value: "", label: "Any stock status" },
          { value: "IN_STOCK", label: "In stock" },
          { value: "LOW_STOCK", label: "Low stock" },
          { value: "OUT_OF_STOCK", label: "Out of stock" },
        ]}
      />
    </div>
  );
}

function Select({
  icon: Icon,
  label,
  value,
  onChange,
  options,
  disabled = false,
  title,
}: {
  icon: typeof Filter;
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  disabled?: boolean;
  title?: string;
}) {
  return (
    <div
      className={cn(
        "relative flex h-touch items-center gap-2 rounded-control border border-line bg-surface pl-3 pr-2",
        "focus-within:border-brand-500",
        disabled && "opacity-55",
      )}
      title={title}
    >
      <Icon className="size-4 shrink-0 text-ink-400" aria-hidden />
      <select
        aria-label={label}
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        // `appearance-none` plus the caret below, because the native caret sits
        // at a different offset in every browser and the row looks ragged.
        className="h-full appearance-none bg-transparent pr-6 text-sm text-ink-900 outline-none disabled:cursor-not-allowed"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <svg
        className="pointer-events-none absolute right-3 size-4 text-ink-400"
        viewBox="0 0 20 20"
        fill="none"
        aria-hidden
      >
        <path
          d="m6 8 4 4 4-4"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
}
